import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import styled from 'styled-components';
import { useQuery } from '@apollo/react-hooks';
import { CSVLink } from 'react-csv';
import { UserContext } from '../../context/UserContext';
import { IconEnum as Icons } from '../Icons';
import { GET_SMS_CAMPAIGNS } from '../../graphql/queries';
import CampaignsTable from './CampaignTable';
import { useMobileMedia } from '../../util/MediaQuery';
import CampaignsList from './mobile_ui/CampaignsList';
import Container from '../../shared/Container';
import Section from '../../shared/Section';
import { AppContext } from '../../context/AppContext';
import useMutateAnalytics from '../../util/mutate.Analytics';
import analyticsKeys from '../../util/UsageStatLinks';
import { CampaignModal, CAMPAIGN_TYPES } from '../../util/Campaign';
import AccessLevels from '../../util/AccessLevels';

export const headers = [
  { label: 'CAMPAIGN NAME', key: 'name' },
  { label: 'STATUS', key: 'status' },
  { label: 'DATE-TIME', key: 'dateTime' },
  { label: 'MESSAGE', key: 'text' },
  { label: 'NUMBER SENT', key: 'numberSent' },
  { label: 'CLICK-THRUS / CONVERSIONS', key: 'totalUrlClicks' },
  { label: 'CREDIT USED', key: 'creditsUsed' },
  { label: 'OPT-OUTs', key: 'optOuts' },
];
const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  position: absolute;
  top: -63px;
  right: 0;
  @media screen and (max-width: 670px) {
    top: -32px;
  }
`;

// const Label = styled.label`
//   font-size: 14px;
//   font-weight: 500;
//   margin-bottom: 5px;
// `;

const SliderContainer = styled.div`
  display: flex;
  border: 1px solid #ddd;
  border-radius: 30px;
  overflow: hidden;
  width: fit-content;
`;

const SliderButton = styled.button`
  padding: 8px 20px;
  background-color: ${(props) => (props.active ? 'rgb(39, 187, 173)' : 'white')};
  color: ${(props) => (props.active ? 'white' : 'rgb(39, 187, 173)')};
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  outline: none;
  transition: background-color 0.3s ease, color 0.3s ease;
  @media screen and (max-width: 670px) {
    padding: 5px 20px;
    font-size: 13px;
  }
  &:hover {
    opacity: 0.9;
  }
`;

export default function Campaigns({ history }) {
  const { user } = useContext(UserContext);
  const isMobile = useMobileMedia();
  const csvRef = useRef();
  const [exportData, setExportData] = useState([]);
  const [allCampaignData, setAllCampaignData] = useState([]);
  const mutateAnalytics = useMutateAnalytics();
  const [campaignType, setCampaignType] = useState(CAMPAIGN_TYPES.General);
  const { setApp } = useContext(AppContext);
  const [isTriggerCampaign, setIsTriggerCampaign] = useState(false);
  const [, setShowModal] = useState(false);

  const { data: campaignsData, loading: campaignsLoading } = useQuery(
    GET_SMS_CAMPAIGNS,
    {
      variables: {
        isTriggerCampaign,
      },
      fetchPolicy: 'network-only',
    },
  );

  const showCsvLink = allCampaignData.length > 0;
  useEffect(() => {
    if (campaignsData) {
      const allCampaigns = campaignsData?.getCampaigns || [];
      setAllCampaignData(allCampaigns);
    }
  }, [campaignsData]);

  const handleCreateNew = () => {
    setShowModal(true);
    const pathData = campaignType === CAMPAIGN_TYPES.General
      ? CampaignModal.GenCampagian
      : CampaignModal.TriggerCampaign;
    history.push({
      pathname: '/campaigns/new',
      search: `?type=${pathData}`,
      state: { campaignType },
    });

    const mutateAnalyticsVariables = {
      link: analyticsKeys.CAMPAIGNS_NEW_BUTTON,
    };
    mutateAnalytics(mutateAnalyticsVariables);
  };

  const getToolBarButtons = () => {
    return [
      {
        icon: Icons.PlusRegular,
        onClick: handleCreateNew,
        text: 'Create New',
        dataHelp: 'campaigns-create-new',
      },
      ...(showCsvLink
        ? [
          {
            icon: Icons.FileExcel,
            onClick: () => {
              const mutateAnalyticsVariables = {
                link: analyticsKeys.CAMPAIGN_DOWNLOAD_CSV,
              };
              mutateAnalytics(mutateAnalyticsVariables);
              downloadExportData();
            },
            text: 'Download CSV',
          },
        ]
        : []),
    ];
  };

  useEffect(() => {
    if (
      exportData.length > 0
      && csvRef
      && csvRef.current
      && csvRef.current.link
    ) {
      setTimeout(() => {
        csvRef.current.link.click();
        setExportData([]);
      });
    }
  }, [exportData]);

  useEffect(() => {
    setApp((old) => ({
      ...old,
      reloadOrg: true,
    }));

    return () => {
      setApp((old) => ({
        ...old,
        reloadOrg: false,
      }));
    };
  }, [setApp]);

  const downloadExportData = () => {
    if (allCampaignData?.length > 0 && csvRef?.current?.link?.click) {
      csvRef.current.link.click();
    }
  };
  const genralHandleChange = () => {
    setCampaignType(CAMPAIGN_TYPES.General);
    setIsTriggerCampaign(false);
  };
  const genralTriggerChange = () => {
    setCampaignType(CAMPAIGN_TYPES.Trigger);
    setIsTriggerCampaign(true);
  };
  return (
    <Container>
      {/* Prev used modal for choose Campaigns*/}
      {/* <BaseModal
        open={showModal}
        modalType={ModalList.TriggerCampaignModal}
        onCloseModal={setShowModal}
        setContentType={setSelecteContent}
      /> */}
      <Section
        headerTitle="Campaigns"
        toolbarItems={getToolBarButtons(history)}
        loading={campaignsLoading}
      >
        <CSVLink
          data={allCampaignData}
          headers={headers}
          filename="campaigns.csv"
          ref={csvRef}
          target="_self"
        />
        {user?.currentOrganizationRole === AccessLevels.SuperAdmin ? (
          <ToggleWrapper>
            <SliderContainer>
              <SliderButton
                active={campaignType === CAMPAIGN_TYPES.General}
                onClick={genralHandleChange}
              >
                {CAMPAIGN_TYPES.General}
              </SliderButton>
              <SliderButton
                active={campaignType === CAMPAIGN_TYPES.Trigger}
                onClick={genralTriggerChange}
              >
                {CAMPAIGN_TYPES.Trigger}
              </SliderButton>
            </SliderContainer>
          </ToggleWrapper>
        ) : null}
        <div data-help="campaigns-list-area">
          {isMobile ? (
            <CampaignsList campaigns={campaignsData?.getCampaigns} />
          ) : (
            <CampaignsTable
              campaigns={campaignsData?.getCampaigns}
              campaignType={campaignType === CAMPAIGN_TYPES.General
                ? CampaignModal.GenCampagian
                : CampaignModal.TriggerCampaign}
            />
          )}
        </div>
      </Section>
    </Container>
  );
}
